import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  writeBatch,
  query,
  orderBy,
  serverTimestamp,
  onSnapshot,
  increment,
  runTransaction,
} from "firebase/firestore";

import { auth, db } from "../firebase";

export const MAX_CHAT_MESSAGE_LENGTH = 4000;

function cleanProfileValue(value, maxLength) {
  return typeof value === "string" ? value.trim().slice(0, maxLength) : "";
}

function chatProfilePatch(profile) {
  if (!profile || typeof profile !== "object") return {};

  const patch = {};
  const userName = cleanProfileValue(
    profile.displayName || profile.name || profile.fullName,
    160,
  );
  const userEmail = cleanProfileValue(profile.email, 254);
  const userRole = cleanProfileValue(profile.category || profile.role, 100);

  if (userName) patch.userName = userName;
  if (userEmail) patch.userEmail = userEmail;
  if (userRole) patch.userRole = userRole;

  return patch;
}

/** Створити чат-док, якщо ще нема */
export async function ensureChat(uidPartner, userProfile) {
  if (!uidPartner) throw new Error("Chat user uid is required");

  const chatRef = doc(db, "chats", uidPartner);
  const snap = await getDoc(chatRef);
  const profilePatch = chatProfilePatch(userProfile);
  if (!snap.exists()) {
    await setDoc(chatRef, {
      participants: ["admin", uidPartner],
      lastMessage: null,
      lastAt: serverTimestamp(),
      unreadForAdmin: 0,
      unreadForUser: 0,
      createdAt: serverTimestamp(),
      ...profilePatch,
    });
  } else {
    const d = snap.data() || {};
    const patch = { ...profilePatch };
    if (typeof d.unreadForAdmin !== "number") patch.unreadForAdmin = 0;
    if (typeof d.unreadForUser !== "number") patch.unreadForUser = 0;
    if (!("lastReadAtAdmin" in d)) patch.lastReadAtAdmin = null;
    if (!("lastReadAtUser" in d)) patch.lastReadAtUser = null;
    if (Object.keys(patch).length) await updateDoc(chatRef, patch);
  }
  return chatRef;
}

/** Надіслати повідомлення */
export async function sendMessage(
  uidPartner,
  { text, from, senderId, userProfile } = {},
) {
  const normalizedText = typeof text === "string" ? text.trim() : "";
  if (!normalizedText) return;
  if (normalizedText.length > MAX_CHAT_MESSAGE_LENGTH) {
    throw new Error(`Message is limited to ${MAX_CHAT_MESSAGE_LENGTH} characters`);
  }
  if (from !== "admin" && from !== "user") {
    throw new Error("Unknown chat sender");
  }

  const currentUid = auth.currentUser?.uid;
  if (!currentUid) throw new Error("Authentication is required");
  if (from === "user" && currentUid !== uidPartner) {
    throw new Error("A user can send messages only to their own chat");
  }

  const chatRef = await ensureChat(uidPartner, userProfile);
  const messageRef = doc(collection(chatRef, "messages"));
  const batch = writeBatch(db);
  const message = {
    text: normalizedText,
    from,
    senderId: senderId || currentUid,
    createdAt: serverTimestamp(),
  };

  if (from === "user") {
    message.telegramNotification = {
      status: "pending",
      attempts: 0,
    };
  }

  batch.set(messageRef, message);

  const updates = {
    lastMessage: { text: normalizedText, from, at: serverTimestamp() },
    lastAt: serverTimestamp(),
    ...chatProfilePatch(userProfile),
  };
  if (from === "admin") updates.unreadForUser = increment(1);
  else updates.unreadForAdmin = increment(1);

  batch.update(chatRef, updates);

  if (from === "user") {
    const queueRef = doc(db, "chatNotificationQueue", messageRef.id);
    batch.set(queueRef, {
      uid: uidPartner,
      messageId: messageRef.id,
      messageRef,
      status: "pending",
      attempts: 0,
      createdAt: serverTimestamp(),
    });
  }

  await batch.commit();
}

/** Поставити прочитаним (скинути лічильник для сторони who: "admin"|"user") */
// export async function markRead(uidPartner, who) {
//   const chatRef = doc(db, "chats", uidPartner);
//   await ensureChat(uidPartner);
//   await updateDoc(chatRef, {
//     unreadForAdmin: who === "admin" ? 0 : undefined,
//     unreadForUser: who === "user" ? 0 : undefined,
//   });
// }
export async function markRead(uidPartner, who) {
  const chatRef = doc(db, "chats", uidPartner);
  await ensureChat(uidPartner);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(chatRef);
    if (!snap.exists()) return;
    const data = {};
    if (who === "admin") {
      data.unreadForAdmin = 0;
      data.lastReadAtAdmin = serverTimestamp();
    } else if (who === "user") {
      data.unreadForUser = 0;
      data.lastReadAtUser = serverTimestamp();
    }
    tx.update(chatRef, data);
  });
}
/** Підписка на стрічку повідомлень (з пагінацією можна доробити пізніше) */
// export function subscribeMessages(uidPartner, cb) {
//   const chatRef = doc(db, "chats", uidPartner);
//   const msgRef = collection(chatRef, "messages");
//   return onSnapshot(
//     // orderBy без індексу не треба, використовує createdAt
//     // імпорт orderBy - якщо користуєтесь, додайте у імпорти: orderBy, query
//     // Або сортуйте на клієнті за createdAt?.seconds
//     msgRef,
//     (snap) => {
//       const list = [];
//       snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
//       list.sort(
//         (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
//       );
//       cb(list);
//     }
//   );
// }
export function subscribeMessages(uidPartner, cb, onError) {
  const chatRef = doc(db, "chats", uidPartner);
  const msgRef = collection(chatRef, "messages");

  const q = query(msgRef, orderBy("createdAt", "asc"));
  return onSnapshot(
    q,
    (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      cb(list);
    },
    onError,
  );
}
/** Підписка на чат-док (для бейджа непрочитаного тощо) */
export function subscribeChatMeta(uidPartner, cb, onError) {
  const chatRef = doc(db, "chats", uidPartner);
  return onSnapshot(
    chatRef,
    (snap) => cb(snap.exists() ? snap.data() : null),
    onError,
  );
}
